import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Container,
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Box,
  Snackbar,
  Alert,
  Paper,
  InputAdornment,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Zoom,
  useScrollTrigger,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import TaobaoProductCard from '../components/TaobaoProductCard';
import { searchTaobaoProducts, importTaobaoProduct, proxiedSupplierImage } from '../services/taobaoService';
import api from '../services/api';

/* ─── Main Component ──────────────────────────────── */
const TaobaoSearchPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /* Tab: 0 = Search, 1 = Keyword Research */
  const [activeTab, setActiveTab] = useState(1);

  /* ── Regular Search state ── */
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [pagination, setPagination] = useState({
    page: 1, pageSize: 12, total: 0, totalPages: 1, sort: 'default', hasMore: true,
  });

  /* ── Bulk selection state ── */
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [searchPricingLoading, setSearchPricingLoading] = useState(false);

  /* ── Keyword Research state ── */
  const [keywordInput, setKeywordInput] = useState('');
  const [kwMinPrice, setKwMinPrice] = useState('');
  const [kwMaxPrice, setKwMaxPrice] = useState('');
  const [kwPageSize, setKwPageSize] = useState(10);
  const [kwResults, setKwResults] = useState([]); // [{ keyword, items, status, error }]
  const [kwRunning, setKwRunning] = useState(false);
  const [kwProgress, setKwProgress] = useState(0);
  const [kwSelected, setKwSelected] = useState(new Set()); // product IDs selected across all keywords
  const [kwBulkLoading, setKwBulkLoading] = useState(false);
  const [kwPricingLoading, setKwPricingLoading] = useState(false);
  const [searchPricing, setSearchPricing] = useState(null);
  const kwAbortRef = useRef(false);

  /* ── Scroll to top ── */
  const showBackToTop = useScrollTrigger({ disableHysteresis: true, threshold: 100 });
  const scrollToTop = useCallback(() => window.scrollTo({ top: 0, behavior: 'smooth' }), []);

  /* ── Infinite scroll ── */
  const handleLoadMoreRef = useRef(null);
  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + document.documentElement.scrollTop + 100 >=
      document.documentElement.offsetHeight &&
      !isLoading && !isLoadingMore && pagination.hasMore
    ) {
      handleLoadMoreRef.current?.();
    }
  }, [isLoading, isLoadingMore, pagination.hasMore]);
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleLoadMore = useCallback(async () => {
    if (isLoading || isLoadingMore || !pagination.hasMore) return;
    const nextPage = pagination.page + 1;
    setIsLoadingMore(true);
    try {
      const minFen = minPrice ? parseFloat(minPrice) * 100 : undefined;
      const maxFen = maxPrice ? parseFloat(maxPrice) * 100 : undefined;
      const data = await searchTaobaoProducts(searchQuery, {
        page: nextPage, pageSize: pagination.pageSize, sort: pagination.sort,
        min_price: minFen, max_price: maxFen,
      });
      if (data?.items?.length > 0) {
        setProducts((prev) => [...prev, ...data.items]);
        setPagination((prev) => ({
          ...prev, page: nextPage,
          hasMore: data.items.length === pagination.pageSize,
          total: data.total || prev.total,
        }));
      } else {
        setPagination((prev) => ({ ...prev, hasMore: false }));
      }
    } catch (err) {
      setError('Failed to load more products');
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoading, isLoadingMore, pagination, searchQuery, minPrice, maxPrice]);

  useEffect(() => { handleLoadMoreRef.current = handleLoadMore; }, [handleLoadMore]);

  /* ── Regular search ── */
  const handleSearch = useCallback(async (newSearch = true) => {
    if (!searchQuery.trim()) return;
    if (newSearch) { setIsLoading(true); setError(''); setSuccess(''); setProducts([]); setSelected(new Set()); setSearchPricing(null); }
    else setIsLoadingMore(true);

    const minFen = minPrice ? parseFloat(minPrice) * 100 : undefined;
    const maxFen = maxPrice ? parseFloat(maxPrice) * 100 : undefined;
    try {
      const data = await searchTaobaoProducts(searchQuery, {
        page: newSearch ? 1 : pagination.page,
        pageSize: pagination.pageSize,
        sort: pagination.sort,
        min_price: minFen, max_price: maxFen,
      });
      if (data?.items?.length > 0) {
        setProducts((prev) => newSearch ? data.items : [...prev, ...data.items]);
        setPagination((prev) => ({
          ...prev, page: newSearch ? 1 : prev.page,
          total: data.total || prev.total,
          hasMore: data.items.length === pagination.pageSize,
        }));
        if (newSearch) setSuccess(`Found ${data.total || data.items.length} products`);
        if (newSearch) {
          setSearchPricingLoading(true);
          try {
            const comparison = await api.post('/api/bulk-research/price-compare', {
              groups: [{ keyword: searchQuery, products: data.items }],
            }, { timeout: 120000 });
            const group = comparison.data?.groups?.[0];
            const byId = new Map((group?.products || []).map((p) => [String(p.id), p]));
            setSearchPricing(group || null);
            setProducts((prev) => prev.map((product) => ({ ...product, pricing: byId.get(String(product.id || product.itemId)) })));
          } catch (comparisonError) {
            console.warn('Google Shopping comparison unavailable:', comparisonError.message);
          } finally {
            setSearchPricingLoading(false);
          }
        }
      } else if (newSearch) {
        setError('No products found. Try a different search term.');
        setProducts([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to search. Please try again.');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [searchQuery, pagination.page, pagination.pageSize, pagination.sort, minPrice, maxPrice]);

  const handleImport = useCallback(async (product) => {
    try {
      setIsLoading(true);
      await importTaobaoProduct(product.item_url || product.detail_url, product);
      setSuccess('Product imported successfully!');
    } catch (err) {
      setError(err.message || 'Failed to import product');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /* ── Bulk selection handlers ── */
  const handleSelectProduct = useCallback((product, checked) => {
    const id = product.id || product.itemId;
    setSelected((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selected.size === products.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(products.map((p) => p.id || p.itemId)));
    }
  }, [products, selected.size]);

  const handleBulkImport = useCallback(async () => {
    const toImport = products.filter((p) => selected.has(p.id || p.itemId));
    if (!toImport.length) return;
    setBulkLoading(true);
    try {
      let ok = 0;
      for (const p of toImport) {
        try {
          await importTaobaoProduct(p.item_url || p.detail_url, p);
          ok++;
        } catch (_) { }
      }
      setSuccess(`Imported ${ok} of ${toImport.length} products.`);
      setSelected(new Set());
    } catch (err) {
      setError('Bulk import failed.');
    } finally {
      setBulkLoading(false);
    }
  }, [products, selected]);

  /* ── Keyword Research ── */
  const parseKeywords = (text) =>
    text.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);

  const handleKwRun = useCallback(async () => {
    const keywords = parseKeywords(keywordInput);
    if (!keywords.length) return;
    kwAbortRef.current = false;
    setKwRunning(true);
    setKwProgress(0);
    setKwResults([]);
    setKwSelected(new Set());
    setKwPricingLoading(false);
    setKwResults(keywords.map((keyword) => ({ keyword, items: [], status: 'loading', error: null })));
    try {
      const response = await api.post('/api/bulk-research/analyze', {
        keywords,
        maxProductsPerKeyword: kwPageSize,
        includeDetails: false,
      }, { timeout: 120000 });
      if (kwAbortRef.current) return;
      const min = kwMinPrice ? Number(kwMinPrice) : null;
      const max = kwMaxPrice ? Number(kwMaxPrice) : null;
      const completedGroups = (response.data?.data?.keywords || []).map((group) => ({
        keyword: group.keyword,
        searchTerms: group.searchTerms || [],
        products: (group.products || []).filter((product) => {
          const price = Number(product.price);
          return (min === null || price >= min) && (max === null || price <= max);
        }).map((product) => ({
          ...product,
          pic_url: proxiedSupplierImage(product.picUrl || product.pic_url || product.mainImageUrl),
          detail_url: product.sourceUrl || product.detail_url,
          shop: { nick: product.sourceSite === '1688' ? '1688' : 'Taobao' },
        })),
      }));
      setKwResults(completedGroups.map((group) => ({ ...group, items: group.products, status: 'done', error: null })));
      setKwProgress(100);
      if (completedGroups.length) {
      try {
        setKwPricingLoading(true);
        const comparison = await api.post('/api/bulk-research/price-compare', { groups: completedGroups }, { timeout: 120000 });
        const byProductId = new Map();
        const pricingByKeyword = new Map();
        for (const group of comparison.data?.groups || []) {
          pricingByKeyword.set(group.keyword, group);
          for (const product of group.products || []) {
            byProductId.set(String(product.id), {
              ...product,
              averagePrice: group.averagePrice,
              marketKeyword: group.marketKeyword,
              sampleSize: group.sampleSize,
              available: group.available,
            });
          }
        }
        setKwResults((previous) => previous.map((group) => ({
          ...group,
          pricing: pricingByKeyword.get(group.keyword) || null,
          items: group.items.map((product) => ({ ...product, pricing: byProductId.get(String(product.id)) })),
        })));
      } catch (error) {
        // Search results remain usable if a market-price lookup is unavailable.
        console.warn('Google Shopping price comparison unavailable:', error.message);
      } finally {
        setKwPricingLoading(false);
      }
      }
    } catch (error) {
      setKwResults((previous) => previous.map((group) => ({ ...group, status: 'error', error: error.message })));
      setError(error.message || 'Product sourcing failed.');
    } finally {
      setKwRunning(false);
    }
  }, [keywordInput, kwMinPrice, kwMaxPrice, kwPageSize]);

  const handleKwSelectProduct = useCallback((product, checked) => {
    const id = product.id || product.itemId;
    setKwSelected((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  }, []);

  const allKwProducts = kwResults.flatMap((r) => r.items);

  const handleKwBulkImport = useCallback(async () => {
    const toImport = allKwProducts.filter((p) => kwSelected.has(p.id || p.itemId));
    if (!toImport.length) return;
    setKwBulkLoading(true);
    try {
      let ok = 0;
      for (const p of toImport) {
        try {
          await importTaobaoProduct(p.item_url || p.detail_url, p);
          ok++;
        } catch (_) { }
      }
      setSuccess(`Imported ${ok} of ${toImport.length} selected products.`);
      setKwSelected(new Set());
    } catch (err) {
      setError('Bulk import failed.');
    } finally {
      setKwBulkLoading(false);
    }
  }, [allKwProducts, kwSelected]);

  /* ─── Render ─────────────────────────────────────── */
  return (
    <Container maxWidth={false} sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Taobao Product Search
        </Typography>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
          <Tab label="Search" />
          <Tab label="Keyword Research" />
        </Tabs>

        {/* ── Search Tab ── */}
        {activeTab === 0 && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth variant="outlined" placeholder="Search for products…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(true)}
                disabled={isLoading}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                  endAdornment: isLoading && <InputAdornment position="end"><CircularProgress size={22} /></InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select value={pagination.sort} label="Sort By" disabled={isLoading}
                  onChange={(e) => { setPagination((p) => ({ ...p, sort: e.target.value, page: 1 })); setTimeout(() => handleSearch(true), 0); }}>
                  <MenuItem value="default">Default</MenuItem>
                  <MenuItem value="price_asc">Price: Low → High</MenuItem>
                  <MenuItem value="price_desc">Price: High → Low</MenuItem>
                  <MenuItem value="sales_desc">Best Selling</MenuItem>
                  <MenuItem value="newest">Newest</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth>
                <InputLabel>Per page</InputLabel>
                <Select value={pagination.pageSize} label="Per page" disabled={isLoading}
                  onChange={(e) => { setPagination((p) => ({ ...p, pageSize: parseInt(e.target.value), page: 1 })); setTimeout(() => handleSearch(true), 0); }}>
                  <MenuItem value={12}>12</MenuItem>
                  <MenuItem value={16}>16</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Button variant="contained" color="primary" fullWidth size="large" sx={{ height: 56 }}
                onClick={() => handleSearch(true)} disabled={isLoading || !searchQuery.trim()}>
                {isLoading ? 'Searching…' : 'Search'}
              </Button>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth variant="outlined" label="Min Price (¥)" type="number"
                value={minPrice} onChange={(e) => setMinPrice(e.target.value)} disabled={isLoading} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth variant="outlined" label="Max Price (¥)" type="number"
                value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} disabled={isLoading} />
            </Grid>
          </Grid>
        )}

        {/* ── Keyword Research Tab ── */}
        {activeTab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline minRows={4}
                label="Paste keywords (one per line or comma-separated)"
                placeholder="wireless earbuds&#10;bluetooth speaker&#10;yoga mat"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                disabled={kwRunning}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth label="Min Price (¥)" type="number"
                value={kwMinPrice} onChange={(e) => setKwMinPrice(e.target.value)} disabled={kwRunning} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth label="Max Price (¥)" type="number"
                value={kwMaxPrice} onChange={(e) => setKwMaxPrice(e.target.value)} disabled={kwRunning} />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Results per keyword</InputLabel>
                <Select value={kwPageSize} label="Results per keyword" disabled={kwRunning}
                  onChange={(e) => setKwPageSize(parseInt(e.target.value))}>
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3} sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" fullWidth size="large" sx={{ height: 56 }}
                onClick={handleKwRun}
                disabled={kwRunning || !keywordInput.trim()}>
                {kwRunning ? 'Running…' : `Search ${parseKeywords(keywordInput).length || ''} Keywords`}
              </Button>
              {kwRunning && (
                <Button variant="outlined" color="error" size="large" sx={{ height: 56, minWidth: 80 }}
                  onClick={() => { kwAbortRef.current = true; setKwRunning(false); }}>
                  Stop
                </Button>
              )}
            </Grid>
            {kwRunning && (
              <Grid item xs={12}>
                <LinearProgress variant="determinate" value={kwProgress} />
                <Typography variant="caption" color="text.secondary">{kwProgress}% complete</Typography>
              </Grid>
            )}
            {kwPricingLoading && (
              <Grid item xs={12}>
                <LinearProgress />
                <Typography variant="caption" color="text.secondary">Comparing each sourcing query with cached Google Shopping averages…</Typography>
              </Grid>
            )}
          </Grid>
        )}
      </Paper>

      {/* ── Search Results ── */}
      {activeTab === 0 && (
        <Box sx={{ width: '100%' }}>
          {products.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {products.length} of {pagination.total.toLocaleString()} products
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={bulkMode} onChange={(e) => { setBulkMode(e.target.checked); setSelected(new Set()); }} />}
                  label={<Typography variant="body2">Bulk Select</Typography>}
                />
                {bulkMode && (
                  <>
                    <Button size="small" variant="outlined" onClick={handleSelectAll}>
                      {selected.size === products.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    {selected.size > 0 && (
                      <Button size="small" variant="contained" color="primary"
                        onClick={handleBulkImport} disabled={bulkLoading}>
                        {bulkLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                        Import {selected.size} selected
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )}

          <Grid container spacing={2}>
            {products.map((product) => (
              <Grid item key={`${product.id || product.itemId}`} xs={12} sm={6} md={4} lg={3}>
                <TaobaoProductCard
                  product={product}
                  onImport={handleImport}
                  disabled={isLoading}
                  selectable={bulkMode}
                  selected={selected.has(product.id || product.itemId)}
                  onSelect={handleSelectProduct}
                />
              </Grid>
            ))}
            {(isLoading || isLoadingMore) && Array(pagination.pageSize).fill(0).map((_, i) => (
              <Grid item key={`sk-${i}`} xs={6} sm={4} md={3} lg={2}>
                <Skeleton variant="rectangular" height={200} sx={{ mb: 1 }} />
                <Skeleton width="80%" height={20} sx={{ mb: 0.5 }} />
                <Skeleton width="60%" height={20} />
              </Grid>
            ))}
          </Grid>

          {!isLoading && !isLoadingMore && products.length === 0 && (
            <Box sx={{ minHeight: '40vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <SearchIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="h6" color="text.secondary">Search for products to get started</Typography>
              <Typography variant="body2" color="text.secondary">Enter a keyword and press Search</Typography>
            </Box>
          )}

          {pagination.hasMore && products.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button variant="outlined" onClick={handleLoadMore}
                disabled={isLoadingMore}
                startIcon={isLoadingMore ? <CircularProgress size={18} /> : null}>
                Load More
              </Button>
            </Box>
          )}
          {searchPricingLoading && <LinearProgress sx={{ mt: 3 }} />}
          {searchPricing?.available && (
            <Paper sx={{ mt: 3, p: 2, borderLeft: '5px solid', borderColor: 'primary.main' }}>
              <Typography variant="subtitle1" fontWeight={700}>Google Shopping benchmark</Typography>
              <Typography variant="body2" color="text.secondary">
                Compared against “{searchPricing.marketKeyword}” · {searchPricing.sampleSize || 0} retail listings · average ${Number(searchPricing.averagePrice).toFixed(2)} USD
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* ── Keyword Research Results ── */}
      {activeTab === 1 && kwResults.length > 0 && (
        <Box>
          {/* Bulk import bar */}
          {kwSelected.size > 0 && (
            <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label={`${kwSelected.size} selected`} color="primary" />
              <Button variant="contained" onClick={handleKwBulkImport} disabled={kwBulkLoading}>
                {kwBulkLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                Import {kwSelected.size} products
              </Button>
              <Button variant="outlined" color="error" onClick={() => setKwSelected(new Set())}>Clear</Button>
            </Paper>
          )}

          {kwResults.map((group, gi) => (
            <Paper key={gi} sx={{ mb: 4, p: { xs: 1.5, md: 2.5 }, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{group.keyword}</Typography>
                  <Typography variant="caption" color="text.secondary">Searched with native handmade supplier terms</Typography>
                  {group.searchTerms?.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                      {group.searchTerms.map((term) => (
                        <Chip key={term} size="small" label={term} variant="outlined" />
                      ))}
                    </Box>
                  )}
                </Box>
                {group.status === 'loading' && <CircularProgress size={16} />}
                {group.status === 'done' && (
                  <Chip size="small" label={`${group.items.length} results`} color="success" variant="outlined" />
                )}
                {group.status === 'error' && (
                  <Chip size="small" label="Failed" color="error" variant="outlined" />
                )}
                {group.pricing?.available && (
                  <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                    <Typography variant="caption" color="text.secondary" display="block">Google Shopping benchmark</Typography>
                    <Typography variant="subtitle2" fontWeight={700}>${Number(group.pricing.averagePrice).toFixed(2)} USD average</Typography>
                    <Typography variant="caption" color="text.secondary">{group.pricing.sampleSize || 0} listings · {group.pricing.marketKeyword}</Typography>
                  </Box>
                )}
              </Box>

              {group.status === 'error' && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>{group.error}</Typography>
              )}

              <Grid container spacing={2}>
                {group.items.map((product) => (
                  <Grid item key={`${gi}-${product.id || product.itemId}`} xs={12} sm={6} md={4} lg={3}>
                    <TaobaoProductCard
                      product={product}
                      onImport={handleImport}
                      disabled={false}
                      selectable={true}
                      selected={kwSelected.has(product.id || product.itemId)}
                      onSelect={handleKwSelectProduct}
                    />
                  </Grid>
                ))}
                {group.status === 'loading' && Array(kwPageSize).fill(0).map((_, i) => (
                  <Grid item key={`sk-${gi}-${i}`} xs={12} sm={6} md={4} lg={3}>
                    <Skeleton variant="rectangular" height={200} sx={{ mb: 1 }} />
                    <Skeleton width="80%" height={20} sx={{ mb: 0.5 }} />
                    <Skeleton width="60%" height={20} />
                  </Grid>
                ))}
                {group.status === 'done' && group.items.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">No products found for this keyword.</Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          ))}
        </Box>
      )}

      {/* Snackbars */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setError('')} severity="error" variant="filled" sx={{ maxWidth: 600 }}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSuccess('')} severity="success" variant="filled" sx={{ maxWidth: 600 }}>{success}</Alert>
      </Snackbar>

      {/* Back to top */}
      <Zoom in={showBackToTop}>
        <Box onClick={scrollToTop} role="button" tabIndex={0} aria-label="Back to top"
          sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1000, cursor: 'pointer', bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 3, '&:hover': { bgcolor: 'primary.dark' } }}>
          <KeyboardArrowUpIcon />
        </Box>
      </Zoom>
    </Container>
  );
};

export default TaobaoSearchPage;
